import React from 'react'
import PropTypes from 'prop-types'
import { AppProvider, Button, Card, Spinner } from '@shopify/polaris'
import GraphqlProvider from '../providers/graphql'
import { getFirebase } from '../helpers/firebase'
import { startSession, removeLegacyCredentials } from '../helpers/shopify'
import { prepareInstallation, prepareSessionInstallation } from '../helpers/auth'
import FirebaseContext from '../providers/firebase'
import '@shopify/polaris/dist/styles.css'

class AppLayout extends React.Component {
    state = { isLoading: true, authError: null, firebase: null, shop: null }
    mounted = false
    pending = null
    installationChecked = false
    redirecting = false
    launchQuery = undefined

    componentDidMount() {
        this.mounted = true
        // HMAC verification needs the original query before URL cleanup.
        this.launchQuery = window.location.search
        removeLegacyCredentials()
        this.getSession()
    }

    componentWillUnmount() { this.mounted = false }

    getSession = () => {
        if (this.redirecting) return this.pending || Promise.resolve()
        if (this.pending) return this.pending
        this.setState({ isLoading: true, authError: null })
        this.pending = (async () => {
            try {
                if (!this.installationChecked) {
                    if (await prepareInstallation(this.launchQuery)) {
                        this.redirecting = true
                        return
                    }
                    this.installationChecked = true
                }
                if (!this.mounted) return
                // Verify Shopify before issuing Firebase credentials. Never
                // restore a custom token from a URL or browser session cache.
                let session
                try {
                    session = await startSession()
                } catch (error) {
                    if (error.response?.data?.error !== 'SHOPIFY_INSTALL_REQUIRED') throw error
                    if (!this.mounted) return
                    if (await prepareSessionInstallation()) {
                        this.redirecting = true
                        return
                    }
                    // Another tab may have completed installation meanwhile.
                    // Retry once; never turn ordinary failures into OAuth loops.
                    session = await startSession()
                }
                const { shop, customToken } = session
                const [app] = await Promise.all([
                    import('firebase/app'), import('firebase/auth'), import('firebase/firestore'),
                ])
                const { firebase } = await getFirebase(app.default, customToken, shop)
                if (this.mounted) this.setState({ shop, firebase, isLoading: false, authError: null })
            } catch (error) {
                const code = error.response?.data?.error || error.code || 'authentication-error'
                console.warn('Shopify app sign-in failed:', code)
                const message = ['SHOPIFY_INSTALL_REQUIRED', 'SHOPIFY_LAUNCH_INVALID'].includes(code)
                    ? 'Please reopen Flype from Shopify Admin to continue installation or sign in.'
                    : code === 'SHOPIFY_BRIDGE_UNAVAILABLE'
                        ? 'We could not connect to Shopify. Open Flype from Shopify Admin and try again.'
                        : 'We could not finish signing you in. Please try again. If this continues, contact Flype support.'
                if (this.mounted) this.setState({ isLoading: false, authError: message })
            } finally {
                this.pending = null
            }
        })()
        return this.pending
    }

    render() {
        const { isLoading, authError, firebase, shop } = this.state
        return <AppProvider>
            {authError ? <Card sectioned title="Unable to sign in">
                <p>{authError}</p>
                <Button onClick={this.getSession}>Try again</Button>
            </Card> : isLoading ? <Card sectioned><Spinner /></Card> :
                <FirebaseContext.Provider value={{ firebase, shop }}>
                    <GraphqlProvider>{this.props.children}</GraphqlProvider>
                </FirebaseContext.Provider>}
        </AppProvider>
    }
}

AppLayout.propTypes = { children: PropTypes.node.isRequired }
export default AppLayout
